/* ==========================================================
   IMPORTECNO THEME — CORE INTERACTIONS
   ========================================================== */
(function () {
  'use strict';

  const routes = { cartUrl: '/cart' };

  /* ---------- Helpers ---------- */
  const qs = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const debounce = (fn, wait) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const fetchJSON = (url, opts) => fetch(url, opts).then((r) => r.json());
  const money = (cents) => {
    try {
      return new Intl.NumberFormat('es-CO', { style: 'currency', currency: window.Shopify?.currency?.active || 'COP', maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(0);
    }
  };

  /* ==========================================================
     CART DRAWER
     ========================================================== */
  const cartDrawer = qs('[data-cart-drawer]');
  const body = document.body;

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    body.classList.add('overlay-scroll-lock');
  }
  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    body.classList.remove('overlay-scroll-lock');
  }
  qsa('[data-cart-trigger]').forEach((el) => el.addEventListener('click', openCartDrawer));
  qsa('[data-cart-close]').forEach((el) => el.addEventListener('click', closeCartDrawer));

  async function refreshCart(cart) {
    if (!cart) cart = await fetchJSON('/cart.js');

    // Item count badge
    qsa('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    // Drawer title count, e.g. "Tu carrito (3)"
    const titleCountEl = qs('[data-cart-title-count]');
    if (titleCountEl) titleCountEl.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';

    // Subtotal
    const subtotalEl = qs('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);

    // Free shipping bar
    const bar = qs('[data-free-shipping-bar]');
    if (bar) {
      const threshold = parseInt(bar.dataset.threshold, 10);
      const remaining = Math.max(threshold - cart.total_price, 0);
      const pct = Math.min((cart.total_price / threshold) * 100, 100);
      const fill = qs('[data-free-shipping-fill]', bar);
      if (fill) fill.style.width = pct + '%';
      const textEl = qs('.free-shipping-bar__text', bar);
      if (textEl) {
        if (remaining === 0) {
          bar.classList.add('is-complete');
          textEl.textContent = textEl.dataset.completeText || '¡Tu pedido tiene envío gratis! 🎉';
        } else {
          bar.classList.remove('is-complete');
          const template = textEl.dataset.template || 'Te faltan {amount} para tu envío gratis 🚚';
          textEl.textContent = template.replace('{amount}', money(remaining));
        }
      }
    }

    // Re-render cart items via cart.js + rebuild DOM (simple approach: reload sections via /cart?section_id would be ideal;
    // here we do a lightweight client-side rebuild for line quantities only, and full reload on add/remove)
    return cart;
  }

  async function addToCart(formData) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'No se pudo añadir el producto.');
    }
    await reloadCartDrawerContents();
    openCartDrawer();
    loadCrossSell();
  }

  async function reloadCartDrawerContents() {
    // Fetch fresh cart and rebuild ONLY the items list. The empty-state
    // message and the checkout footer are never rebuilt or rewritten by
    // JS — they're the original Shopify-rendered HTML, just shown/hidden.
    const cart = await fetchJSON('/cart.js');
    const itemsWrap = qs('[data-cart-items]');
    const emptyState = qs('.cart-empty');
    const footer = qs('[data-cart-footer]');
    if (!itemsWrap) return refreshCart(cart);

    if (cart.item_count === 0) {
      itemsWrap.hidden = true;
      if (footer) footer.hidden = true;
      if (emptyState) emptyState.hidden = false;
    } else {
      if (emptyState) emptyState.hidden = true;
      itemsWrap.hidden = false;
      if (footer) footer.hidden = false;

      itemsWrap.innerHTML = cart.items
        .map(
          (item, i) => `
        <div class="cart-item" data-cart-item data-line="${i + 1}" data-variant-id="${item.variant_id}">
          <a href="${item.url}" class="cart-item__image"><img src="${item.image}" alt="" loading="lazy"></a>
          <div class="cart-item__info">
            <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
            <div class="cart-item__footer">
              <div class="qty-selector">
                <button type="button" data-qty-decrease aria-label="Disminuir">−</button>
                <input type="number" value="${item.quantity}" min="0" data-qty-input aria-label="Cantidad">
                <button type="button" data-qty-increase aria-label="Aumentar">+</button>
              </div>
              <span class="price">${money(item.final_line_price)}</span>
            </div>
            <button class="cart-item__remove" data-cart-remove>Eliminar</button>
          </div>
        </div>`
        )
        .join('') + `<div class="cart-cross-sell" data-cart-cross-sell><h3>✨ Combina perfecto con:</h3><div data-cross-sell-items></div></div>`;
    }
    bindCartItemEvents();
    refreshCart(cart);
  }

  function bindCartItemEvents() {
    qsa('[data-cart-item]').forEach((row) => {
      const variantId = row.dataset.variantId;
      const input = qs('[data-qty-input]', row);
      const dec = qs('[data-qty-decrease]', row);
      const inc = qs('[data-qty-increase]', row);
      const removeBtn = qs('[data-cart-remove]', row);

      const update = debounce(async (qty) => {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        });
        reloadCartDrawerContents();
      }, 350);

      dec?.addEventListener('click', () => {
        const val = Math.max(parseInt(input.value, 10) - 1, 0);
        input.value = val;
        update(val);
      });
      inc?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) + 1;
        input.value = val;
        update(val);
      });
      input?.addEventListener('change', () => update(Math.max(parseInt(input.value, 10) || 0, 0)));
      removeBtn?.addEventListener('click', () => update(0));
    });
  }
  bindCartItemEvents();

  qs('body')?.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = qs('[data-add-to-cart]', form);
    const originalText = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span>';
    }
    try {
      await addToCart(new FormData(form));
    } catch (err) {
      alert(err.message);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = originalText;
      }
    }
  });
    /* ---------- Cross-sell recommendations in cart drawer ---------- */
  async function loadCrossSell() {
    const wrap = qs('[data-cross-sell-items]');
    if (!wrap) return;
    try {
      const cart = await fetchJSON('/cart.js');
      if (!cart.items.length) return;
      const productId = cart.items[0].product_id;
      const data = await fetchJSON(`/recommendations/products.json?product_id=${productId}&limit=3&intent=complementary`);
      wrap.innerHTML = data.products
        .map(
          (p) => `
        <div class="cart-cross-sell__item">
          <img src="${p.featured_image}" alt="">
          <div class="info"><div class="t">${p.title}</div><div class="p">${money(p.price)}</div></div>
          <button class="btn-icon btn-sm" data-cross-sell-add data-variant-id="${p.variants[0].id}" aria-label="Añadir">+</button>
        </div>`
        )
        .join('');
      qsa('[data-cross-sell-add]', wrap).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const fd = new FormData();
          fd.append('id', btn.dataset.variantId);
          fd.append('quantity', 1);
          await addToCart(fd);
        });
      });
    } catch (e) {
      /* fail silently */
    }
  }

  /* ==========================================================
     PREDICTIVE / AI SEARCH
     ========================================================== */
  const searchDrawer = qs('[data-search-drawer]');
  function openSearch() {
    if (!searchDrawer) return;
    searchDrawer.classList.add('is-open');
    body.classList.add('overlay-scroll-lock');
    setTimeout(() => qs('[data-search-input]')?.focus(), 100);
  }
  function closeSearch() {
    if (!searchDrawer) return;
    searchDrawer.classList.remove('is-open');
    body.classList.remove('overlay-scroll-lock');
  }
  qsa('[data-search-trigger]').forEach((el) => el.addEventListener('click', openSearch));
  qsa('[data-search-close]').forEach((el) => el.addEventListener('click', closeSearch));

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      openSearch();
    }
    if (e.key === 'Escape') {
      closeSearch();
      closeCartDrawer();
      qs('[data-mobile-nav]')?.classList.remove('is-open');
    }
  });

  qsa('[data-search-suggestion]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = qs('[data-search-input]');
      input.value = btn.textContent.trim();
      input.dispatchEvent(new Event('input'));
    });
  });

  const searchInput = qs('[data-search-input]');
  const runSearch = debounce(async (term) => {
    const defaultView = qs('[data-search-default]');
    const resultsView = qs('[data-search-results]');
    const grid = qs('[data-search-results-grid]');
    const empty = qs('[data-search-empty]');
    if (!term) {
      defaultView.hidden = false;
      resultsView.hidden = true;
      return;
    }
    try {
      const data = await fetchJSON(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=8&resources[options][fields]=title,vendor,product_type,tag`);
      const products = data.resources.results.products || [];
      defaultView.hidden = true;
      resultsView.hidden = false;
      empty.hidden = products.length > 0;
      grid.innerHTML = products
        .map(
          (p) => `
        <a class="product-card product-card--shadow" href="${p.url}">
          <div class="product-card__media"><img src="${p.featured_image?.url || p.image}" alt="${p.title}" loading="lazy"></div>
          <div class="product-card__body">
            <div class="product-card__vendor">${p.vendor || ''}</div>
            <h3 class="product-card__title">${p.title}</h3>
            <div class="price">${p.price}</div>
          </div>
        </a>`
        )
        .join('');
    } catch (e) {
      /* noop */
    }
  }, 280);

  searchInput?.addEventListener('input', (e) => runSearch(e.target.value.trim()));

  /* ==========================================================
     PRODUCT PAGE — variant selection, gallery, quantity
     ========================================================== */
  const productRoot = qs('[data-product-json]');
  if (productRoot) {
    const productData = JSON.parse(productRoot.dataset.productJson);

    // Gallery
    qsa('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        qs('[data-gallery-main]').src = thumb.dataset.full;
        qsa('[data-gallery-thumb]').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });

    // Quantity selector
    bindQtySelector(productRoot);

    // Variant/options
    const optionButtons = qsa('[data-option-value]', productRoot);
    const selected = {};
    optionButtons.forEach((btn) => {
      if (btn.classList.contains('is-selected')) selected[btn.dataset.optionPosition] = btn.dataset.value;
    });

    function findVariant() {
      return productData.variants.find((v) => {
        return [1, 2, 3].every((pos) => {
          if (!selected[pos]) return true;
          const key = 'option' + pos;
          return v[key] === selected[pos];
        });
      });
    }

    function updateForVariant(variant) {
      if (!variant) return;
      const priceWrap = qs('[data-product-price]');
      if (priceWrap) {
        let html = `<span class="price">${money(variant.price)}</span>`;
        if (variant.compare_at_price > variant.price) {
          html += `<span class="price price--compare">${money(variant.compare_at_price)}</span>`;
        }
        priceWrap.innerHTML = html;
      }
      const stockWrap = qs('[data-product-stock]');
      if (stockWrap) {
        stockWrap.innerHTML = `<span class="dot"></span>${variant.available ? 'En stock, listo para enviar' : 'Agotado temporalmente'}`;
      }
      const addBtn = qs('[data-add-to-cart]');
      if (addBtn) {
        addBtn.disabled = !variant.available;
        addBtn.innerHTML = variant.available ? '🛒 Añadir al carrito' : 'Agotado';
      }
      const idInput = qs('[data-variant-id-input]');
      if (idInput) idInput.value = variant.id;

      if (variant.featured_image) {
        qs('[data-gallery-main]').src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_1200x$1$2');
      }
    }

    optionButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.swatch-list');
        qsa('.swatch', group).forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        selected[btn.dataset.optionPosition] = btn.dataset.value;
        const labelEl = btn.closest('.option-selector').querySelector('[data-selected-value]');
        if (labelEl) labelEl.textContent = btn.dataset.value;
        updateForVariant(findVariant());
      });
    });

    qs('[data-buy-now]')?.addEventListener('click', async () => {
      const form = qs('[data-product-form]');
      await addToCart(new FormData(form));
      window.location.href = '/checkout';
    });
  }

  function bindQtySelector(ctx) {
    qsa('.qty-selector', ctx).forEach((sel) => {
      const input = qs('[data-qty-input]', sel);
      qs('[data-qty-decrease]', sel)?.addEventListener('click', () => {
        input.value = Math.max((parseInt(input.value, 10) || 1) - 1, 1);
      });
      qs('[data-qty-increase]', sel)?.addEventListener('click', () => {
        input.value = (parseInt(input.value, 10) || 1) + 1;
      });
    });
  }
    /* ==========================================================
     QUICK ADD FROM PRODUCT CARDS
     ========================================================== */
  const quickBuyModal = qs('[data-quick-buy-modal]');
  function openQuickBuy() { quickBuyModal?.classList.add('is-open'); body.classList.add('overlay-scroll-lock'); }
  function closeQuickBuy() { quickBuyModal?.classList.remove('is-open'); body.classList.remove('overlay-scroll-lock'); }
  qsa('[data-quick-buy-close]').forEach((el) => el.addEventListener('click', closeQuickBuy));

  document.addEventListener('click', async (e) => {
    const trigger = e.target.closest('[data-quick-add]');
    if (!trigger) return;
    e.preventDefault();
    const handle = trigger.dataset.productHandle;
    const content = qs('[data-quick-buy-content]');
    content.innerHTML = '<div class="spinner" style="margin:40px auto;"></div>';
    openQuickBuy();
    try {
      const product = await fetchJSON(`/products/${handle}.js`);
      if (product.variants.length === 1) {
        const fd = new FormData();
        fd.append('id', product.variants[0].id);
        fd.append('quantity', 1);
        await addToCart(fd);
        closeQuickBuy();
        return;
      }
      content.innerHTML = `
        <img src="${product.featured_image}" alt="" style="width:100%;aspect-ratio:1/1;object-fit:cover;border-radius:14px;margin-bottom:16px;">
        <h3>${product.title}</h3>
        <div class="price" style="font-size:1.3rem;margin-bottom:16px;">${money(product.price)}</div>
        <select data-quick-variant-select style="width:100%;padding:12px;border:1px solid var(--color-border);border-radius:10px;margin-bottom:16px;">
          ${product.variants.map((v) => `<option value="${v.id}" ${!v.available ? 'disabled' : ''}>${v.title}${!v.available ? ' (agotado)' : ''}</option>`).join('')}
        </select>
        <button class="btn btn-primary btn-full" data-quick-buy-add>Añadir al carrito</button>
      `;
      qs('[data-quick-buy-add]').addEventListener('click', async () => {
        const variantId = qs('[data-quick-variant-select]').value;
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', 1);
        await addToCart(fd);
        closeQuickBuy();
      });
    } catch (err) {
      content.innerHTML = '<p>No se pudo cargar el producto.</p>';
    }
  });

  /* ==========================================================
     DESKTOP MEGA MENU
     ========================================================== */
  const navItems = qsa('[data-nav-item]');
  function closeAllNavItems(except) {
    navItems.forEach((item) => {
      if (item !== except) {
        item.classList.remove('is-open');
        qs('[data-nav-trigger]', item)?.setAttribute('aria-expanded', 'false');
      }
    });
  }
  navItems.forEach((item) => {
    const trigger = qs('[data-nav-trigger]', item);
    let closeTimer;
    const open = () => {
      clearTimeout(closeTimer);
      closeAllNavItems(item);
      item.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
    };
    const scheduleClose = () => {
      closeTimer = setTimeout(() => {
        item.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
      }, 150);
    };
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);
    trigger?.addEventListener('click', () => {
      item.classList.contains('is-open') ? scheduleClose() : open();
    });
    trigger?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') scheduleClose();
    });
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-nav-item]')) closeAllNavItems();
  });

  /* ==========================================================
     HERO SLIDER (multi-banner carousel)
     ========================================================== */
  qsa('[data-hero-slider]').forEach((slider) => {
    const slides = qsa('[data-hero-slide]', slider);
    const dots = qsa('[data-hero-dot]', slider);
    const prevBtn = qs('[data-hero-prev]', slider);
    const nextBtn = qs('[data-hero-next]', slider);
    if (slides.length <= 1) return;

    let current = slides.findIndex((s) => s.classList.contains('is-active'));
    if (current < 0) current = 0;
    let timer = null;
    const autoplay = slider.dataset.autoplay === 'true';
    const speed = (parseInt(slider.dataset.autoplaySpeed, 10) || 5) * 1000;

    function goTo(index) {
      slides[current].classList.remove('is-active');
      dots[current] && dots[current].classList.remove('is-active');
      current = (index + slides.length) % slides.length;
      slides[current].classList.add('is-active');
      dots[current] && dots[current].classList.add('is-active');
    }
    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }
    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      timer = setInterval(next, speed);
    }
    function stopAutoplay() { if (timer) clearInterval(timer); }

    nextBtn && nextBtn.addEventListener('click', () => { next(); startAutoplay(); });
    prevBtn && prevBtn.addEventListener('click', () => { prev(); startAutoplay(); });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => { goTo(parseInt(dot.dataset.index, 10)); startAutoplay(); });
    });

    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', startAutoplay);

    let touchStartX = 0;
    slider.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    slider.addEventListener('touchend', (e) => {
      const diff = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? prev() : next();
        startAutoplay();
      }
    }, { passive: true });

    startAutoplay();
  });

  /* ==========================================================
     MOBILE NAV DRAWER + FILTERS
     ========================================================== */
  const mobileNav = qs('[data-mobile-nav]');
  function openMobileNav() {
    mobileNav?.classList.add('is-open');
    mobileNav?.setAttribute('aria-hidden', 'false');
    body.classList.add('overlay-scroll-lock');
  }
  function closeMobileNav() {
    mobileNav?.classList.remove('is-open');
    mobileNav?.setAttribute('aria-hidden', 'true');
    body.classList.remove('overlay-scroll-lock');
  }
  qsa('[data-mobile-nav-toggle]').forEach((btn) => btn.addEventListener('click', openMobileNav));
  qsa('[data-mobile-nav-close]').forEach((el) => el.addEventListener('click', closeMobileNav));
  const filtersPanel = qs('[data-filters]');
  qsa('[data-mobile-filter-trigger]').forEach((btn) => btn.addEventListener('click', () => filtersPanel?.classList.add('is-open')));
  qsa('[data-mobile-filter-close]').forEach((btn) => btn.addEventListener('click', () => filtersPanel?.classList.remove('is-open')));

  /* Auto-submit sort */
  qs('[data-sort-select]')?.addEventListener('change', function () {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.value);
    window.location.href = url.toString();
  });

  /* Auto-submit filters on checkbox change */
  qsa('[data-filter-input]').forEach((input) => {
    input.addEventListener('change', () => {
      qs('[data-filter-form]')?.requestSubmit();
    });
  });

  /* ==========================================================
     ANNOUNCEMENT BAR ROTATION
     ========================================================== */
  const announcementSlides = qsa('.announcement-bar__slide');
  if (announcementSlides.length > 1) {
    let i = 0;
    setInterval(() => {
      announcementSlides[i].hidden = true;
      i = (i + 1) % announcementSlides.length;
      announcementSlides[i].hidden = false;
    }, 4000);
  }

  /* Load cross-sell on initial cart drawer open if items exist */
  document.addEventListener('DOMContentLoaded', () => {
    if (qs('[data-cart-items] [data-cart-item]')) loadCrossSell();
  });
})();